import { Data, Route, Routes } from '@angular/router';

import { AppErrorComponent } from '@pages/http/5XX/app.error.component';
import { AppNotfoundComponent } from '@pages/http/4XX/app.notfound.component';
import { AppShellComponent } from '@app-layout/shell';

import { authGuard, GuestData, guestGuard, PrivilegeData } from '@core/guards';
import { SystemSettingsResolver } from '@core/resolvers/system-settings/system-settings.resolver';
import { authDataResolver } from '@core/resolvers/auth-data/auth-data.resolver';
import { evaluationResolver } from '@core/resolvers/evaluation/evaluation.resolver';
import { guestResolver } from '@core/resolvers/guest/guest.resolver';
import { MessageData } from '@shared/components/display/page-layouts/message-page/message-page.component';
import { AccessDeniedPath } from '@core/constants';
import { AppAccessdeniedComponent } from '@pages/http/4XX/app.accessdenied.component';
import { evaluationGuard } from '@core/guards/evaluation/evaluation.guard';
import { runtimeConfigResolver } from '@core/resolvers/runtime-config.resolver';
import { pendoResolver } from '@core/resolvers';
import { GuestSessionService, SESSION_SERVICE } from '@core/services';

export interface NymblRoute extends Route {
  data?: MessageData | (PrivilegeData & GuestData);
}

export type NymblRoutes = NymblRoute[];

export const appRoutes: NymblRoutes = [
  { path: 'error', component: AppErrorComponent },
  { path: 'notfound', component: AppNotfoundComponent },
  {
    path: AccessDeniedPath,
    title: 'Access Denied',
    component: AppAccessdeniedComponent
  },
  {
    path: 'success',
    title: 'Successfully Submitted LMN',
    loadComponent: () =>
      import('./features/patients/lmn/evaluation/success-screen/success-screen.component').then(
        (c) => c.SuccessScreenComponent
      )
  },
  {
    path: 'lmn-goodbye',
    title: 'Logged Out',
    loadComponent: () =>
      import('./shared/components/display/page-layouts/message-page/message-page.component').then(
        (c) => c.MessagePageComponent
      ),
    data: {
      messageSummary: 'Logged Out Due to Inactivity.',
      messageDetail:
        'Please contact the CRT Supplier who sent you this link for assistance in generating a new link.'
    }
  },
  {
    path: 'lmn-link-expired',
    title: 'Link Expired',
    loadComponent: () =>
      import('./shared/components/display/page-layouts/message-page/message-page.component').then(
        (c) => c.MessagePageComponent
      ),
    data: {
      messageSummary: 'This link is no longer active.',
      messageDetail:
        'A new link has been sent to your email. Please check your inbox and use the most recent link to continue.'
    }
  },
  {
    path: 'link-expired',
    title: 'Link Expired',
    loadComponent: () =>
      import('./shared/components/display/page-layouts/message-page/message-page.component').then(
        (c) => c.MessagePageComponent
      ),
    data: {
      messageSummary: 'This link is no longer active.',
      messageDetail: 'Please contact support if you believe this is an error.'
    }
  },
  {
    path: 'lmn-therapist',
    component: AppShellComponent,
    canActivate: [authGuard],
    resolve: {
      auth: guestResolver
    },
    // A guest therapist has a guest token and no credentials: extend through
    // the guest-user refresh endpoint, and end at lmn-goodbye rather than a
    // login screen they could not use. Scoped to this route so the rest of the
    // app keeps the default user-session strategy.
    providers: [{ provide: SESSION_SERVICE, useExisting: GuestSessionService }],
    children: [
      {
        path: '',
        component: AppErrorComponent
      },
      {
        path: ':evalId',
        title: 'LMN Portal',
        canActivate: [guestGuard, evaluationGuard],
        data: { guestType: 'THERAPIST' },
        loadComponent: () =>
          import('./features/patients/lmn/evaluation/evaluation.component').then(
            (c) => c.LMNEvaluationComponent
          ),
        resolve: {
          lmnData: evaluationResolver,
          config: runtimeConfigResolver,
          pendo: pendoResolver
        }
      }
    ]
  },
  {
    path: '',
    component: AppShellComponent,
    canActivateChild: [authGuard, guestGuard],
    resolve: {
      config: runtimeConfigResolver,
      enabledPages: SystemSettingsResolver,
      auth: authDataResolver,
      pendo: pendoResolver
    },
    data: {
      systemSettings: [
        'ai_notes_on',
        'parachute_enabled',
        'hide_device_type',
        'enable_batch_sign',
        'enable_bulk_claims',
        'stripe_pay_on',
        'pickup_delivery_appointment_automation',
        'enable_new_purchasing_inventory'
      ],
      guestType: undefined
    },
    loadChildren: () =>
      import('./app-layout/main/app.main.routes').then((m) => m.mainRoutes)
  },

  { path: '**', redirectTo: '/notfound' }
];
